import {useHttp} from "@inertiajs/vue3";
import Cookies from "js-cookie";

/**
 * Payload keys which are shadowed by the form state of `useHttp()`.
 *
 * `useHttp()` spreads the payload into an object literal which afterwards defines its own
 * methods and status flags, so the internal member always wins. A payload field named like
 * one of them would therefore be dropped from the request body without any error - see
 * `useFormState()` in `node_modules/@inertiajs/vue3/dist/index.js`.
 *
 * @type {Set<string>}
 */
const shadowedPayloadKeys = new Set([
  "cancel",
  "clearErrors",
  "data",
  "defaults",
  "delete",
  "dontRemember",
  "errors",
  "get",
  "hasErrors",
  "isDirty",
  "optimistic",
  "patch",
  "post",
  "processing",
  "progress",
  "put",
  "recentlySuccessful",
  "reset",
  "resetAndClearErrors",
  "response",
  "setError",
  "submit",
  "transform",
  "wasSuccessful",
  "withAllErrors",
  "withPrecognition",
]);

const shadowedKeyPrefix = "shadowed_payload_key_";

function withAuthHeaders(options = {}) {
  const token = Cookies.get("mc_token");

  if (!token) return options;

  return {
    ...options,
    headers: {
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  };
}

/**
 * Index of the payload within the arguments of `useHttp()`, which accepts them as
 * `(payload)`, `(rememberKey, payload)`, `(urlMethodPair, payload)` or `(method, url, payload)`.
 *
 * @see UseFormUtils.parseUseFormArguments()
 */
function payloadArgumentIndex(args) {
  if (args.length === 0) return -1;
  if (args.length === 1) return 0;

  return Math.min(args.length - 1, 2);
}

function mapKeys(payload, mapKey) {
  return Object.fromEntries(Object.entries(payload).map(([key, value]) => [mapKey(key), value]));
}

/** Renames the payload keys which would be shadowed by the form state of `useHttp()`. */
function escapeShadowedKeys(payload) {
  if (typeof payload === "function") {
    return () => escapeShadowedKeys(payload());
  }

  if (payload === null || typeof payload !== "object" || Array.isArray(payload)) {
    return payload;
  }

  return mapKeys(payload, (key) => (shadowedPayloadKeys.has(key) ? shadowedKeyPrefix + key : key));
}

/** Restores the original key names, so the server receives the payload as it has been passed in. */
function restoreShadowedKeys(payload) {
  return mapKeys(payload, (key) => (key.startsWith(shadowedKeyPrefix) ? key.slice(shadowedKeyPrefix.length) : key));
}

const noTransform = (payload) => payload;

export function useAuthHttp(...args) {
  const index = payloadArgumentIndex(args);

  if (index >= 0) {
    args[index] = escapeShadowedKeys(args[index]);
  }

  const http = useHttp(...args);

  // the escaped keys have to be restored on every request, so the transform of
  // useHttp is occupied by us and a transform of the caller is chained after ours
  let callerTransform = noTransform;

  http.transform((payload) => callerTransform(restoreShadowedKeys(payload)));

  http.transform = (callback) => {
    callerTransform = callback;

    return http;
  };

  for (const method of ["get", "post", "put", "patch", "delete"]) {
    const original = http[method].bind(http);
    http[method] = (url, options = {}) => original(url, withAuthHeaders(options));
  }

  return http;
}
